import { useEffect, useState } from "react";
import { Link } from "react-router";
import { Skeleton } from "primereact/skeleton";
import { humanDateWithoutTime } from "sj-ui-toolkit/src/utils/dates";
import { AdminAppRoutes } from "sj-ui-toolkit/src/constants/routes";
import type { AssignmentPolicyDetail } from "@/api/assignments";
import { loadPathByIdAsync } from "@/api/assignments";
import {
  normalizeNotificationsFrequency,
  parseAssignmentDueDate,
  parseAssignmentProgress,
  reminderTextMap,
  capitalize,
} from "@/helpers/assignmentHelpers";
import { AssignmentProgressChart } from "@/components/assignments/AssignmentProgressChart";

interface AssignmentOverviewTabProps {
  policy: AssignmentPolicyDetail | null;
}

interface LinkedPath {
  id: string;
  name: string;
}

/** Read-only stats for an assignment: progress chart + due date, cadence, audiences, total. */
export const AssignmentOverviewTab = ({ policy }: AssignmentOverviewTabProps) => {
  const roleId = policy?.roleId;
  const [path, setPath] = useState<LinkedPath | null>(null);
  const [isLoadingPath, setIsLoadingPath] = useState(true);

  useEffect(() => {
    if (!roleId) {
      setPath(null);
      setIsLoadingPath(false);
      return;
    }
    let cancelled = false;
    setIsLoadingPath(true);

    loadPathByIdAsync(roleId)
      .then(data => {
        if (cancelled) return;
        const role = data.role ?? data;
        setPath(role.id && role.name ? { id: String(role.id), name: role.name } : null);
      })
      .catch(() => {
        if (!cancelled) setPath(null);
      })
      .finally(() => {
        if (!cancelled) setIsLoadingPath(false);
      });

    return () => {
      cancelled = true;
    };
  }, [roleId]);

  if (!policy) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <h2 className="atlas-h5 mb-4">Progress</h2>
          <div className="flex justify-center">
            <Skeleton width="320px" height="320px" />
          </div>
        </div>
        <div className="flex flex-col gap-8">
          <div>
            <Skeleton width="8rem" height="1rem" className="mb-2" />
            <Skeleton width="6rem" height="2.5rem" />
          </div>
          <div className="grid grid-cols-2 gap-8">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i}>
                <Skeleton width="8rem" height="1rem" className="mb-2" />
                <Skeleton width="6rem" height="2.5rem" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const dueDate = parseAssignmentDueDate(policy.dueDateAt);
  // A dynamic (relative per-user) due date has no fixed dueDateAt but carries a duration.
  const isDynamicDueDate = !dueDate && !!policy.duration && policy.duration > 0;
  const dueDateLabel = dueDate
    ? humanDateWithoutTime(dueDate)
    : isDynamicDueDate
      ? "Dynamic"
      : "No due date";
  const cadenceKey = capitalize(normalizeNotificationsFrequency(policy.notificationsFrequency));
  const progress = parseAssignmentProgress(policy.progress);
  const engagementRate =
    progress.total > 0
      ? Math.round(((progress.inProgress + progress.complete) / progress.total) * 100)
      : 0;
  const stats = policy.assignmentStats;
  const completionRate =
    stats && stats.lessonsAssigned > 0
      ? Math.round((stats.lessonsPassed / stats.lessonsAssigned) * 100)
      : 0;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      <div>
        <h2 className="atlas-h5 mb-4">Progress</h2>
        <AssignmentProgressChart progress={progress} />
      </div>
      <div className="flex flex-col gap-8">
        <div>
          <h3 className="atlas-h5 text-color-secondary mb-2">Path</h3>
          {isLoadingPath ? (
            <Skeleton width="6rem" height="2.5rem" />
          ) : path ? (
            <Link
              to={AdminAppRoutes.routes.paths.routes.detail.route.replace(":id", path.id)}
              className="atlas-h2"
            >
              {path.name}
            </Link>
          ) : (
            <p className="atlas-h2">{"\u2014"}</p>
          )}
        </div>
        <div className="grid grid-cols-2 gap-8">
          <div>
            <h3 className="atlas-h5 text-color-secondary mb-2">Lesson Completion</h3>
            <p className="atlas-h2">{`${completionRate}%`}</p>
          </div>
          <div>
            <h3 className="atlas-h5 text-color-secondary mb-2">Engagement Rate</h3>
            <p className="atlas-h2">{`${engagementRate}%`}</p>
          </div>
          <div>
            <h3 className="atlas-h5 text-color-secondary mb-2">Total Assigned</h3>
            <p className="atlas-h2">{progress.total}</p>
          </div>
          <div>
            <h3 className="atlas-h5 text-color-secondary mb-2">Due Date</h3>
            <p className="atlas-h2">{dueDateLabel}</p>
          </div>
          <div>
            <h3 className="atlas-h5 text-color-secondary mb-2">Audiences</h3>
            <p className="atlas-h2">{policy.audiences?.map(a => a.name).join(", ") || "\u2014"}</p>
          </div>
          <div>
            <h3 className="atlas-h5 text-color-secondary mb-2">Notification Cadence</h3>
            <p className="atlas-h2">{cadenceKey}</p>
            <p className="text-secondary body-sm">{reminderTextMap[cadenceKey]}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssignmentOverviewTab;
